import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Pencil,
  Save,
  X,
  User,
  Building2,
  MapPin,
  Phone,
  Briefcase,
  Heart,
  Landmark,
  ShieldCheck,
  RefreshCw,
  ChevronLeft,
  AlertTriangle,
  UserCheck,
  Mail,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { useAccess } from "../../../utils/useAccess";
import { PERMS } from "../../../utils/perms";

import {
  useGetAdminDetailsQuery,
  useUpdateAdminOrgMutation,
  useUpdateAdminPhoneMutation,
  useUpdateAdminEmailMutation,
} from "../api/adminsApi";
import { useGetDepartmentsQuery } from "../../department/api/departmentsApi";
import { useGetDesignationsQuery } from "../../designation/api/designationsApi";

type Option = { id: number; name: string };

function val(v: any) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return v;
}

function Row({ k, v }: { k: string; v: any }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-50 py-3 text-sm last:border-0">
      <div className="text-gray-400 shrink-0">{k}</div>
      <div className="text-gray-800 font-semibold text-right break-all">
        {val(v)}
      </div>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
  className = "",
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`bg-white border border-gray-100 rounded-2xl p-5 shadow-sm ${className}`}
    >
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-50">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="font-black text-gray-900 text-[10px] uppercase tracking-widest">
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

function InlineEdit({
  label,
  display,
  editing,
}: {
  label: string;
  display: React.ReactNode;
  editing?: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-50 py-3 text-sm items-center last:border-0">
      <div className="text-gray-400 shrink-0">{label}</div>
      <div className="text-right">{editing ?? display}</div>
    </div>
  );
}

export default function AdminDetailsPage() {
  const { id } = useParams();
  const adminId = String(id || "");
  const { can } = useAccess();

  const {
    data: res,
    isLoading,
    error,
    refetch,
  } = useGetAdminDetailsQuery(adminId, { skip: !adminId });
  const data = useMemo(
    () => ((res as any)?.data ? (res as any).data : res),
    [res],
  );

  const profileImageUrl = useMemo(() => {
    const p = data?.user?.pro_pic;
    if (!p) return null;
    if (p.startsWith("http")) return p;
    return `https://api.niqsng.org/storage/${p.replace(/^\/+/, "")}`;
  }, [data]);

  const fullName = useMemo(() => {
    if (!data) return "";
    return [data.firstname, data.othername, data.lastname]
      .filter(Boolean)
      .join(" ");
  }, [data]);

  const isActive = data?.status === "active" || data?.user?.status === "active";

  // Dropdown data
  const { data: deptsRaw = [] } = useGetDepartmentsQuery();
  const { data: desigsRaw = [] } = useGetDesignationsQuery();
  const departments: Option[] = useMemo(
    () => deptsRaw.map((d: any) => ({ id: Number(d.id), name: d.name })),
    [deptsRaw],
  );
  const designations: Option[] = useMemo(
    () => desigsRaw.map((d: any) => ({ id: Number(d.id), name: d.name })),
    [desigsRaw],
  );

  // Mutations
  const [updateOrg, { isLoading: savingOrg }] = useUpdateAdminOrgMutation();
  const [updatePhone, { isLoading: savingPhone }] =
    useUpdateAdminPhoneMutation();
  const [updateEmail, { isLoading: savingEmail }] =
    useUpdateAdminEmailMutation();

  // Edit states
  const [editingOrg, setEditingOrg] = useState(false);
  const [deptId, setDeptId] = useState<number>(0);
  const [desigId, setDesigId] = useState<number>(0);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phone, setPhone] = useState("");
  const [phone2, setPhone2] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    if (!data) return;
    setDeptId(Number(data.department_id || data.department?.id || 0));
    setDesigId(Number(data.designation_id || data.designation?.id || 0));
    setPhone(data.phone || "");
    setPhone2(data.phone2 || "");
    setEmail(data.email || data.user?.email || "");
  }, [data]);

  const saveOrg = async () => {
    try {
      await updateOrg({
        id: adminId,
        department_id: deptId,
        designation_id: desigId,
      }).unwrap();
      setMsg({ type: "success", text: "Organisation updated successfully." });
      setEditingOrg(false);
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: e?.data?.message || "Update failed." });
    }
  };

  const savePhone = async () => {
    try {
      await updatePhone({ id: adminId, phone, phone2 }).unwrap();
      setMsg({ type: "success", text: "Phone numbers updated." });
      setEditingPhone(false);
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: e?.data?.message || "Update failed." });
    }
  };

  const saveEmail = async () => {
    try {
      await updateEmail({ id: adminId, email }).unwrap();
      setMsg({ type: "success", text: "Login email updated." });
      setEditingEmail(false);
      refetch();
    } catch (e: any) {
      setMsg({ type: "error", text: e?.data?.message || "Update failed." });
    }
  };

  if (isLoading) {
    return (
      <div className="py-32 flex flex-col items-center gap-3 text-gray-400">
        <RefreshCw className="w-8 h-8 animate-spin" />
        <span className="text-sm font-medium">Loading admin profile…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-20 flex flex-col items-center gap-4 text-center">
        <AlertTriangle className="w-10 h-10 text-red-400" />
        <p className="text-gray-600 font-medium">
          Failed to load admin details.
        </p>
        <button
          onClick={() => refetch()}
          className="px-5 py-2 bg-gray-900 text-white rounded-xl text-sm font-bold"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <PageGate perm={PERMS.admin.detailsAccess}>
      <div className="space-y-6">
        {/* ── Hero Header ── */}
        <div className="bg-gray-900 rounded-3xl p-8 text-white relative overflow-hidden">
          {/* faint background icon */}
          <User className="absolute -right-6 -top-6 w-48 h-48 text-white/5 pointer-events-none" />

          <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-white/10 bg-white/10 flex items-center justify-center">
                {profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl font-black text-white/60">
                    {data.firstname?.[0]}
                    {data.lastname?.[0]}
                  </span>
                )}
              </div>
              {/* status dot */}
              <div
                className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-gray-900 ${
                  isActive ? "bg-green-400" : "bg-gray-500"
                }`}
              />
            </div>

            {/* Name + badges */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl font-black tracking-tight">{fullName}</h1>
              <p className="text-white/50 text-sm mt-0.5">{data.user?.email}</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-3">
                {data.staff_no && (
                  <span className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                    {data.staff_no}
                  </span>
                )}
                {data.designation?.name && (
                  <span className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                    {data.designation.name}
                  </span>
                )}
                {data.department?.name && (
                  <span className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                    {data.department.name}
                  </span>
                )}
                <span
                  className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    isActive
                      ? "bg-green-500/20 text-green-400"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 shrink-0">
              <Link
                to="/admins"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-bold transition-all"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </Link>
              <button
                onClick={() => refetch()}
                className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition-all"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Alert / success bar */}
          {msg && (
            <div
              className={`mt-6 relative z-10 flex items-center justify-between gap-3 px-5 py-3 rounded-xl text-sm font-medium ${
                msg.type === "success"
                  ? "bg-green-500/15 border border-green-500/20 text-green-400"
                  : "bg-red-500/15 border border-red-500/20 text-red-400"
              }`}
            >
              <span>{msg.text}</span>
              <button onClick={() => setMsg(null)}>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* ── Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Organisation & Account */}
          <Section title="Organisation & Account" icon={Building2}>
            {/* Email */}
            <InlineEdit
              label="Login Email"
              display={
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800 text-sm">
                    {data.user?.email || data.email || "—"}
                  </span>
                  <AccessGate perm={PERMS.admin.changeEmail}>
                    <button
                      onClick={() => setEditingEmail(true)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Pencil className="w-3 h-3 text-blue-500" />
                    </button>
                  </AccessGate>
                </div>
              }
              editing={
                editingEmail ? (
                  <div className="flex gap-1.5 items-center">
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      className="border rounded-lg px-2 py-1 text-xs outline-none focus:ring-1 ring-gray-900 w-40"
                    />
                    <button
                      onClick={saveEmail}
                      disabled={savingEmail}
                      className="bg-gray-900 text-white p-1.5 rounded-lg"
                    >
                      <Save className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setEditingEmail(false)}
                      className="border p-1.5 rounded-lg"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : undefined
              }
            />

            {/* Department */}
            <InlineEdit
              label="Department"
              display={
                <span className="font-semibold text-gray-800 text-sm">
                  {data.department?.name || "—"}
                </span>
              }
              editing={
                editingOrg ? (
                  <select
                    value={deptId}
                    onChange={(e) => setDeptId(Number(e.target.value))}
                    className="text-xs border rounded-lg p-1.5 outline-none"
                  >
                    <option value={0}>Select department</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                ) : undefined
              }
            />

            {/* Designation */}
            <InlineEdit
              label="Designation"
              display={
                <span className="font-semibold text-gray-800 text-sm">
                  {data.designation?.name || "—"}
                </span>
              }
              editing={
                editingOrg ? (
                  <select
                    value={desigId}
                    onChange={(e) => setDesigId(Number(e.target.value))}
                    className="text-xs border rounded-lg p-1.5 outline-none"
                  >
                    <option value={0}>Select designation</option>
                    {designations.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                ) : undefined
              }
            />

            <Row k="Work Email" v={data.work_email} />
            <Row k="Staff No." v={data.staff_no} />

            <div className="mt-3 flex justify-end">
              <AccessGate perm={PERMS.admin.changeOrg}>
                {!editingOrg ? (
                  <button
                    onClick={() => setEditingOrg(true)}
                    className="text-[10px] font-black text-blue-600 uppercase tracking-wider hover:underline"
                  >
                    Reassign Dept / Designation
                  </button>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={() => setEditingOrg(false)}
                      className="text-[10px] font-black text-gray-400 uppercase tracking-wider"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveOrg}
                      disabled={savingOrg}
                      className="text-[10px] font-black text-green-600 uppercase tracking-wider"
                    >
                      {savingOrg ? "Saving…" : "Save Changes"}
                    </button>
                  </div>
                )}
              </AccessGate>
            </div>
          </Section>

          {/* Identity & Personal */}
          <Section title="Identity & Personal" icon={User}>
            <Row k="Gender" v={data.gender} />
            <Row k="Date of Birth" v={data.dob?.slice(0, 10)} />
            <Row k="Marital Status" v={data.marital_status} />
            <Row k="Nationality" v={data.nationality} />
            <Row k="State of Origin" v={data.state_of_origin} />
            <Row k="LGA of Origin" v={data.lga} />
          </Section>

          {/* Contact Details */}
          <Section title="Contact Details" icon={Phone}>
            <InlineEdit
              label="Phone Numbers"
              display={
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800 text-sm">
                    {data.phone
                      ? `${data.phone}${data.phone2 ? ` / ${data.phone2}` : ""}`
                      : "—"}
                  </span>
                  <AccessGate perm={PERMS.admin.updatePhone}>
                    <button
                      onClick={() => setEditingPhone(true)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Pencil className="w-3 h-3 text-blue-500" />
                    </button>
                  </AccessGate>
                </div>
              }
              editing={
                editingPhone ? (
                  <div className="flex flex-col gap-1.5">
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="border rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 ring-gray-900"
                      placeholder="Primary phone"
                    />
                    <input
                      value={phone2}
                      onChange={(e) => setPhone2(e.target.value)}
                      className="border rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 ring-gray-900"
                      placeholder="Secondary phone"
                    />
                    <div className="flex gap-1.5 justify-end mt-1">
                      <button
                        onClick={savePhone}
                        disabled={savingPhone}
                        className="bg-gray-900 text-white px-3 py-1 rounded-lg text-xs font-bold"
                      >
                        {savingPhone ? "Saving…" : "Save"}
                      </button>
                      <button
                        onClick={() => setEditingPhone(false)}
                        className="border px-3 py-1 rounded-lg text-xs font-bold text-gray-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : undefined
              }
            />
          </Section>

          {/* Employment */}
          <Section title="Employment" icon={Briefcase}>
            <Row k="Job Title" v={data.job_title} />
            <Row k="Employment Type" v={data.employment_type} />
            <Row k="Date Joined" v={data.employment_date?.slice(0, 10)} />
          </Section>

          {/* Address */}
          <Section title="Contact Address" icon={MapPin}>
            <Row k="Line 1" v={data.address_line1} />
            <Row k="Line 2" v={data.address_line2} />
            <Row k="City" v={data.city} />
            <Row k="State" v={data.state} />
            <Row k="LGA" v={(data as any).contact_lga} />
            <Row k="Postal Code" v={data.postal_code} />
            <Row k="Country" v={data.country} />
          </Section>

          {/* Emergency Contact */}
          <Section title="Emergency Contact" icon={AlertTriangle}>
            <Row k="Name" v={(data as any).emergency_name} />
            <Row k="Relationship" v={(data as any).emergency_relationship} />
            <Row k="Phone" v={(data as any).emergency_phone} />
            <Row k="Address" v={(data as any).emergency_address} />
          </Section>

          {/* Next of Kin */}
          <Section title="Next of Kin" icon={UserCheck}>
            <Row k="Name" v={(data as any).nok_name} />
            <Row k="Relationship" v={(data as any).nok_relationship} />
            <Row k="Phone" v={(data as any).nok_phone} />
            <Row k="Email" v={(data as any).nok_email} />
          </Section>

          {/* Bank / Payroll */}
          <Section title="Payroll & Banking" icon={Landmark}>
            <Row k="Bank Name" v={data.bank_name} />
            <Row k="Account Name" v={(data as any).bank_account_name} />
            <Row k="Account Number" v={data.bank_account_number} />
            <Row k="Sort Code" v={(data as any).bank_sort_code} />
          </Section>

          {/* Notes */}
          <div className="lg:col-span-2 bg-gray-900 text-white rounded-2xl p-6 relative overflow-hidden">
            <ShieldCheck className="absolute -right-4 -bottom-4 w-32 h-32 text-white/5 pointer-events-none" />
            <div className="flex items-center gap-2 mb-3 relative z-10">
              <ShieldCheck className="w-4 h-4 text-white/40" />
              <span className="text-[10px] font-black uppercase tracking-widest text-white/40">
                Administrative Notes
              </span>
            </div>
            <p className="text-sm leading-relaxed text-gray-300 italic relative z-10">
              {data.notes ||
                "No special instructions or administrative notes recorded for this personnel."}
            </p>
          </div>
        </div>
      </div>
    </PageGate>
  );
}
